import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    // Users,
    Award,
    Heart,
    Shield,
    Lightbulb,
    Headphones,
    Star,
    ArrowUpRight,
    Quote,
    Sparkles,
} from "lucide-react";
import AnimatedSection, {
    StaggerContainer,
    StaggerItem,
} from "../../components/ui/AnimatedSection";

const values = [
    {
        icon: Heart,
        title: "Client-Centric",
        description:
            "Your goals are our priority. We put your interests first in every decision.",
    },
    {
        icon: Shield,
        title: "Trust & Transparency",
        description:
            "Honest communication and complete transparency in all our dealings.",
    },
    {
        icon: Lightbulb,
        title: "Innovation",
        description:
            "Continuously evolving our strategies to leverage modern financial solutions.",
    },
    {
        icon: Headphones,
        title: "Dedicated Support",
        description:
            "Always available to assist you with your financial queries and concerns.",
    },
];

// const milestones = [
//     {
//         year: "2008",
//         event: "Founded",
//         desc: "Started with a vision to make financial planning accessible",
//     },
//     {
//         year: "2012",
//         event: "Expansion",
//         desc: "Added investment advisory and wealth management services",
//     },
//     {
//         year: "2016",
//         event: "1000+ Clients",
//         desc: "Milestone of serving over 1000 happy families",
//     },
//     {
//         year: "2020",
//         event: "Digital Launch",
//         desc: "Launched online services for seamless experience",
//     },
//     {
//         year: "2023",
//         event: "₹500Cr+ AUM",
//         desc: "Crossed ₹500 crores in assets under management",
//     },
// ];

// const team = [
//     {
//         name: "Rahul Sharma",
//         role: "Founder & CEO",
//         bio: "20+ years in financial services",
//         initials: "RS",
//     },
//     {
//         name: "Priya Mehta",
//         role: "Head of Insurance",
//         bio: "IRDA certified with 15+ years",
//         initials: "PM",
//     },
//     {
//         name: "Vikram Singh",
//         role: "Investment Director",
//         bio: "CFA charterholder",
//         initials: "VS",
//     },
//     {
//         name: "Anita Desai",
//         role: "Client Relations",
//         bio: "Exceptional client experience",
//         initials: "AD",
//     },
// ];

const testimonials = [
    {
        name: "Rajesh Patel",
        role: "Business Owner",
        content:
            "Seva Consultancy helped us find the perfect health insurance plan. Their guidance was invaluable.",
        initials: "RP",
    },
    {
        name: "Sneha Mehta",
        role: "IT Professional",
        content:
            "Their investment advice has been exceptional. My portfolio has grown significantly.",
        initials: "SM",
    },
    {
        name: "Amit Kumar",
        role: "Engineer",
        content:
            "Professional, knowledgeable, and always available. Financial planning made simple.",
        initials: "AK",
    },
];

const stats = [
    { value: "5000+", label: "Happy Clients" },
    { value: "15+", label: "Years Excellence" },
    { value: "₹500Cr+", label: "Assets Managed" },
    { value: "100%", label: "Satisfaction" },
];

export default function AboutPage() {
    return (
        <div className="overflow-hidden">
            {/* Hero */}
            <section className="relative py-32 bg-cream">
                <div className="absolute inset-0 bg-pattern" />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#d4b86a]/5 rounded-full blur-3xl" />

                <div className="container-custom px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl mx-auto text-center"
                    >
                        <span className="badge mb-4">
                            <Sparkles className="w-3 h-3 mr-2" />
                            About Us
                        </span>
                        <h1 className="text-gray-900 mb-6">
                            Your Trusted
                            <span className="block mt-1 gradient-text">
                                Financial Partner
                            </span>
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            With over 15 years of experience, we've helped
                            thousands of families secure their financial future
                            through expert guidance and personalized solutions.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="py-12 bg-white border-y border-gray-100">
                <div className="container-custom px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center"
                            >
                                <p className="text-3xl md:text-4xl font-display gradient-text mb-0.5">
                                    {stat.value}
                                </p>
                                <p className="text-xs uppercase tracking-wider text-gray-500">
                                    {stat.label}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <AnimatedSection direction="left">
                            <div className="relative">
                                <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                                    <img
                                        src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070"
                                        alt="Our Team"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="absolute -bottom-6 -right-6 bg-gradient-to-br from-[#c9a84c] to-[#d4b86a] text-white rounded-xl p-5 shadow-lg"
                                >
                                    <Award className="w-7 h-7 mb-1" />
                                    <p className="font-display font-semibold">
                                        Since 2008
                                    </p>
                                    <p className="text-xs opacity-80">
                                        Trusted Excellence
                                    </p>
                                </motion.div>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection direction="right">
                            <span className="badge mb-4">Our Story</span>
                            <h2 className="text-gray-900 mb-4">
                                A Legacy of Trust
                            </h2>
                            <div className="space-y-4 text-gray-600 leading-relaxed">
                                <p>
                                    Founded in 2008, Seva Consultancy was born
                                    from a simple belief: everyone deserves
                                    access to quality financial guidance. What
                                    started as a small advisory has grown into a
                                    comprehensive financial services firm.
                                </p>
                                <p>
                                    Our journey has been defined by unwavering
                                    commitment to client success. We don't just
                                    offer products – we build lasting
                                    relationships based on trust, transparency,
                                    and deep understanding of each client's
                                    needs.
                                </p>
                                <p>
                                    Today, we offer a complete suite of services
                                    from insurance to investments, all delivered
                                    with the personal touch that has been our
                                    hallmark since day one.
                                </p>
                            </div>
                        </AnimatedSection>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-16">
                        <span className="badge mb-4">Our Values</span>
                        <h2 className="text-gray-900 mb-2">What Drives Us</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Our core values shape every interaction and decision
                            we make
                        </p>
                    </AnimatedSection>

                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value) => (
                            <StaggerItem key={value.title}>
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    className="glass-card-hover rounded-xl p-6 text-center h-full"
                                >
                                    <div className="icon-box mx-auto mb-5">
                                        <value.icon className="w-6 h-6 text-[#c9a84c]" />
                                    </div>
                                    <h3 className="text-base font-display text-gray-900 mb-2">
                                        {value.title}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        {value.description}
                                    </p>
                                </motion.div>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Testimonials */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-16">
                        <span className="badge mb-4">Testimonials</span>
                        <h2 className="text-gray-900 mb-2">
                            Client{" "}
                            <span className="gradient-text">Experiences</span>
                        </h2>
                    </AnimatedSection>

                    <StaggerContainer className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((testimonial) => (
                            <StaggerItem key={testimonial.name}>
                                <motion.div
                                    whileHover={{ y: -5 }}
                                    className="glass-card rounded-xl p-6 h-full relative"
                                >
                                    <Quote className="absolute top-4 right-4 w-8 h-8 text-[#d4b86a]/10" />

                                    <div className="flex text-[#d4b86a] mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className="w-4 h-4 fill-current"
                                            />
                                        ))}
                                    </div>

                                    <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                                        "{testimonial.content}"
                                    </p>

                                    <div className="flex items-center">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c9a84c] to-[#d4b86a] flex items-center justify-center mr-3">
                                            <span className="text-white text-sm font-semibold">
                                                {testimonial.initials}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-gray-900 font-medium text-sm">
                                                {testimonial.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {testimonial.role}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Team */}
            {/* <section className="section-padding bg-white relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4b86a]/30 to-transparent" />

                <div className="container-custom relative z-10">
                    <AnimatedSection className="text-center mb-16">
                        <span className="badge mb-4">Our Team</span>
                        <h2 className="text-gray-900 mb-2">Meet the Experts</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Certified professionals with decades of combined
                            experience
                        </p>
                    </AnimatedSection>

                    <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {team.map((member) => (
                            <StaggerItem key={member.name}>
                                <motion.div
                                    whileHover={{ y: -6 }}
                                    className="glass-card rounded-xl p-6 text-center"
                                >
                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#c9a84c] to-[#d4b86a] flex items-center justify-center mx-auto mb-4">
                                        <span className="text-white text-xl font-semibold">
                                            {member.initials}
                                        </span>
                                    </div>
                                    <h3 className="text-base font-display text-gray-900 mb-0.5">
                                        {member.name}
                                    </h3>
                                    <p className="text-xs uppercase tracking-wider text-[#c9a84c] mb-2">
                                        {member.role}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {member.bio}
                                    </p>
                                </motion.div>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section> */}

            {/* CTA */}
            <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 section-dark">
                <div className="container-custom px-6 text-center relative z-10">
                    <AnimatedSection>
                        <h2 className="text-white mb-4">
                            Ready to Begin Your{" "}
                            <span className="gradient-text">Journey</span>?
                        </h2>
                        <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                            Let's discuss how we can help you achieve your
                            financial goals
                        </p>
                        <Link to="/contact">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-primary inline-flex items-center space-x-2"
                            >
                                <span>Get in Touch</span>
                                <ArrowUpRight className="w-4 h-4" />
                            </motion.button>
                        </Link>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
}
